/**
 * Enquiries API Service
 * Handles all enquiry-related API calls
 */

import API_BASE_URL from './apiConfig';
import { getAuthHeaders, getUser } from './authApi';
import { formatDate } from '../utils/dateUtils';
import {
  buildEnquiriesListCacheKey,
  getCachedEnquiriesList,
  setCachedEnquiriesList,
  clearEnquiriesListCache,
} from '../utils/enquiriesListCache';
import {
  getCachedEmployeeCounts,
  setCachedEmployeeCounts,
  getCachedFilterOptions,
  setCachedFilterOptions,
  clearEnquiriesAuxiliaryCache,
} from '../utils/enquiriesAuxiliaryCache';
import dayjs from 'dayjs';

/**
 * Transform backend enquiry data to frontend format.
 * All fields needed for Edit Enquiry form must be included so prefill works correctly.
 */
const transformEnquiry = (enquiry) => {
  const city = enquiry.city ?? enquiry.full_address?.city ?? '';
  const state = enquiry.state ?? enquiry.full_address?.state ?? '';
  const country = enquiry.country ?? enquiry.full_address?.country ?? '';
  return {
    id: enquiry._id,
    sn: 0, // Will be set based on index
    enquiryNumber: enquiry.enquiry_number || enquiry._id,
    status: enquiry.current_status || '',
    lastUpdatedOn: formatDate(enquiry.updatedAt),
    clientName: enquiry.client_name || '',
    location: city || enquiry.full_address?.city || '',
    locationDisplay: [city, state].filter(Boolean).join(', ') || '--',
    initialEnquiryDate: formatDate(enquiry.enquiry_date),
    enquiry_date: enquiry.enquiry_date || '', // Raw for edit form (YYYY-MM-DD)
    mainProductType: enquiry.main_product_type || '',
    nextFollowUpOn: enquiry.next_follow_up_on || '',
    main_product_type: enquiry.main_product_type || '',
    main_product_type_slug: enquiry.main_product_type_slug || '',
    client_id: enquiry.client_id?._id ?? enquiry.client_id ?? null,
    discussions: enquiry.updates?.length || 0,
    requirements: enquiry.requirement || [],
    requirementsSummary: (enquiry.requirement || []).map(req =>
      `${req.item_name || 'Item'} (Qty: ${req.item_quantity || 0})`
    ).join(', ') || 'No requirements',
    client_email: enquiry.client_email || '',
    client_mobile: enquiry.client_mobile || '',
    city,
    state,
    country,
    country_code: enquiry.country_code || '',
    medium: enquiry.medium || '',
    source: enquiry.source || '',
    source_comment: enquiry.source_comment || '',
    requirement: enquiry.requirement || [],
    assigned_employee_current_display: enquiry.assigned_employee_current_display || '',
    assigned_employee_current: enquiry.assigned_employee_current || '',
    assigned_employees_till_date: enquiry.assigned_employees_till_date || [],
    updates: enquiry.updates || [],
    next_follow_up_on: enquiry.next_follow_up_on || '',
    comment: enquiry.comment || '',
    assignedTo: enquiry.assigned_employee_current_display
      ? `${enquiry.assigned_employee_current_display}`
      : (enquiry.assigned_employee_current || ''),
    createdAt: enquiry.createdAt,
    _id: enquiry._id,
  };
};

/**
 * Transform frontend enquiry data to backend format
 */
const transformEnquiryForBackend = (enquiry) => {
  return {
    enquiry_number: enquiry.enquiry_number,
    client_name: enquiry.client_name || enquiry.clientName,
    client_id: enquiry.client_id,
    client_email: enquiry.client_email,
    client_mobile: enquiry.client_mobile,
    full_address: enquiry.full_address,
    city: enquiry.city,
    state: enquiry.state,
    contact_person_name: enquiry.contact_person_name,
    contact_person_designation: enquiry.contact_person_designation,
    enquiry_date: enquiry.enquiry_date,
    main_product_type: enquiry.main_product_type,
    main_product_type_slug: enquiry.main_product_type_slug,
    planning_for_date: enquiry.planning_for_date,
    requirement: enquiry.requirement,
    rfq: enquiry.rfq,
    rfq_docs: enquiry.rfq_docs,
    other_docs: enquiry.other_docs,
    current_status: enquiry.current_status || enquiry.status,
    is_quotation_sent: enquiry.is_quotation_sent,
    quotation_doc: enquiry.quotation_doc,
    medium: enquiry.medium,
    source: enquiry.source,
    source_comment: enquiry.source_comment,
    comment: enquiry.comment,
    // Employee ID for audit/assignment, expected to be injected from auth-aware caller
    employee: enquiry.employee,
    assigned_employee_current: enquiry.assigned_employee_current,
    assigned_employee_current_display: enquiry.assigned_employee_current_display,
    // Persist next follow-up date at root-level as required by backend contract
    next_follow_up_on: enquiry.next_follow_up_on,
    // Pass through updates array as-is; caller is responsible for immutably appending new entries
    // Expected shape for status updates:
    // { date, comment, discussion, new_status, type }
    updates: enquiry.updates,
    created_by: enquiry.created_by,
  };
};

/**
 * List enquiries with filters, sorting, and pagination
 * @param {Object} [options]
 * @param {boolean} [options.skipCache] — force network (after caller clears cache)
 * @param {string} [options.cacheUserId] — scope cache per user (e.g. employee_id)
 */
export const listEnquiries = async (filters = {}, sort = {}, page = 0, rowsPerPage = 10, options = {}) => {
  const { skipCache = false, cacheUserId = '' } = options;
  const cacheKey = buildEnquiriesListCacheKey(filters, sort, page, rowsPerPage, cacheUserId);

  if (!skipCache) {
    const cached = getCachedEnquiriesList(cacheKey);
    if (cached) return cached;
  }

  try {
    const params = new URLSearchParams({
      page: (page + 1).toString(),
      limit: rowsPerPage.toString(),
    });

    const dateRange = filters.dateRange || {};
    if (dateRange.from) {
      params.append('fromDate', dayjs(dateRange.from).format('YYYY-MM-DD'));
    }
    if (dateRange.to) {
      params.append('toDate', dayjs(dateRange.to).format('YYYY-MM-DD'));
    }
    if (filters.city) {
      params.append('city', filters.city);
    }
    if (filters.state) {
      params.append('state', filters.state);
    }
    if (filters.client_id) {
      params.append('client_id', filters.client_id);
    }
    if (filters.client_name || filters.clientName) {
      params.append('client_name', filters.client_name || filters.clientName);
    }
    if (filters.status) {
      params.append('status', filters.status);
    }
    if (filters.enquiry_number || filters.enquiryNumber) {
      params.append('enquiry_number', filters.enquiry_number || filters.enquiryNumber);
    }
    if (filters.employee) {
      params.append('employee', filters.employee);
    }

    const response = await fetch(`${API_BASE_URL}/enquiry?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    const enquiries = result.data || [];
    const transformed = enquiries.map((enquiry, index) => ({
      ...transformEnquiry(enquiry),
      sn: page * rowsPerPage + index + 1,
    }));

    const payload = {
      data: transformed,
      total: result.total || 0,
      page,
      rowsPerPage,
    };
    if (!skipCache) {
      setCachedEnquiriesList(cacheKey, payload);
    }
    return payload;
  } catch (error) {
    console.error('Error fetching enquiries:', error);
    throw error;
  }
};

/**
 * Get single enquiry by ID
 */
export const getEnquiryById = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/enquiry/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const enquiry = await response.json();
    return transformEnquiry(enquiry);
  } catch (error) {
    console.error('Error fetching enquiry:', error);
    throw error;
  }
};

/**
 * Create new enquiry
 */
export const createEnquiry = async (data) => {
  try {
    const payload = transformEnquiryForBackend(data);
    
    const response = await fetch(`${API_BASE_URL}/enquiry`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
      throw new Error(errorData.message || 'Failed to create enquiry');
    }

    const result = await response.json();

    return {
      success: true,
      message: 'Enquiry created successfully',
      data: transformEnquiry(result),
    };
  } catch (error) {
    console.error('Error creating enquiry:', error);
    return {
      success: false,
      error: error.message || 'Failed to create enquiry',
    };
  }
};

/**
 * Update enquiry
 */
export const updateEnquiry = async (id, updates) => {
  try {
    const payload = transformEnquiryForBackend(updates);
    
    const response = await fetch(`${API_BASE_URL}/enquiry/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
      throw new Error(errorData.message || 'Failed to update enquiry');
    }

    const result = await response.json();

    return {
      success: true,
      data: transformEnquiry(result),
    };
  } catch (error) {
    console.error('Error updating enquiry:', error);
    return {
      success: false,
      error: error.message || 'Failed to update enquiry',
    };
  }
};

/**
 * Delete enquiry
 */
export const deleteEnquiry = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/enquiry/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });

    if (!response.ok) {
      throw new Error('Failed to delete enquiry');
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error('Error deleting enquiry:', error);
    return {
      success: false,
      error: error.message || 'Failed to delete enquiry',
    };
  }
};

/**
 * Mark enquiry as quotation sent (transition to quotation)
 */
export const markQuotationSent = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/enquiry/mark-quotation-sent/${id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to mark quotation sent');
    }

    const result = await response.json();
    return {
      success: true,
      message: result.message || 'Quotation marked as sent',
      data: result.quotation || result,
    };
  } catch (error) {
    console.error('Error marking enquiry quotation sent:', error);
    return {
      success: false,
      error: error.message || 'Failed to mark quotation sent',
    };
  }
};

/**
 * Get enquiry counts by employee (Admin only)
 * @param {Object} [options]
 * @param {boolean} [options.skipCache]
 * @param {string} [options.cacheUserId]
 */
export const getEmployeeEnquiryCounts = async (options = {}) => {
  const { skipCache = false, cacheUserId } = options;
  const uid = cacheUserId !== undefined ? cacheUserId : (getUser()?.employee_id || '');

  if (!skipCache) {
    const cached = getCachedEmployeeCounts(uid);
    if (cached !== null) return cached;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/enquiry/employee-counts`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
      throw new Error(errorData.message || 'Failed to fetch employee counts');
    }

    const data = await response.json();
    if (!skipCache) {
      setCachedEmployeeCounts(uid, data);
    }
    return data;
  } catch (error) {
    console.error('Error fetching employee enquiry counts:', error);
    return [];
  }
};

/**
 * Get unique cities and states for enquiry filters
 * @param {Object} [options]
 * @param {boolean} [options.skipCache]
 * @param {string} [options.cacheUserId]
 */
export const getFilterOptions = async (options = {}) => {
  const { skipCache = false, cacheUserId } = options;
  const uid = cacheUserId !== undefined ? cacheUserId : (getUser()?.employee_id || '');

  if (!skipCache) {
    const cached = getCachedFilterOptions(uid);
    if (cached) return cached;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/enquiry/filter-options`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
      throw new Error(errorData.message || 'Failed to fetch filter options');
    }

    const data = await response.json();
    const payload = { cities: data.cities || [], states: data.states || [] };
    if (!skipCache) {
      setCachedFilterOptions(uid, payload);
    }
    return payload;
  } catch (error) {
    console.error('Error fetching filter options:', error);
    return { cities: [], states: [] };
  }
};

// Export as default object for backward compatibility
const enquiriesApi = {
  list: listEnquiries,
  get: getEnquiryById,
  create: createEnquiry,
  update: updateEnquiry,
  delete: deleteEnquiry,
  markQuotationSent,
  getEmployeeEnquiryCounts,
  getFilterOptions,
  clearListCache: clearEnquiriesListCache,
  clearAuxiliaryCaches: clearEnquiriesAuxiliaryCache,
};

export default enquiriesApi;
